import React, { useRef } from 'react';
import { useObservable } from 'react-use-observable';

import { FilterOutlined as FilterOutlineIcon } from '@ant-design/icons';
import { Row, Col, Button } from 'antd';

import isAfter from 'date-fns/isAfter';
import startOfDay from 'date-fns/startOfDay';
import { Form as FormikForm, Field, useFormikContext } from 'formik';
import type { FieldProps } from 'formik';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import type { ISelectProductMultipleHandles } from 'v2/components/Shared/FormFields/SelectProduct';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import useGetUser from 'v2/hooks/useGetUser';
import { EDateTypes } from 'v2/interfaces/reports/refunds';
import type { IRefundFilter } from 'v2/interfaces/reports/refunds';
import { initialFilter, refundsService } from 'v2/services/reports/refunds';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';

interface IMotive {
  id?: string;
  description: string;
}

const dateTypes = [
  { label: 'Pagamento', value: EDateTypes.PAID },
  { label: 'Reembolso', value: EDateTypes.REFUNDED }
];

export const Form = () => {
  const user = useGetUser();

  const [motives, motivesError] = useObservable<IMotive[]>(
    () => refundRequestFactory.getRefundMotivesFromBoApi(user?.id),
    []
  );

  const selectProductRef = useRef<ISelectProductMultipleHandles>(null);

  const { isValid, isSubmitting, resetForm, dirty } = useFormikContext<IRefundFilter>();

  const [isLoading] = useObservable(() => refundsService.isLoading(), []);

  const isOutsideRange = (day: Date) => isAfter(startOfDay(day), startOfDay(new Date()));

  const handleClean = () => {
    resetForm({ values: initialFilter });
    selectProductRef.current?.unCheckAll();
  };

  const motivesOptions = (motives || []).map(item => ({
    label: item.description,
    value: item.id
  }));

  const loadingMotivesOptions = motivesError === null && motives === null;
  const isCleanDisabled = !dirty || isSubmitting || isLoading;
  const isFilterDisabled = !isValid || isSubmitting || isLoading;

  return (
    <FormikForm>
      <div id='refunds-filter' className='mb-4 flex w-full max-sm:flex-wrap'>
        <Row gutter={[16, 16]} className='flex-1'>
          <Col xs={24} md={12}>
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={12} md={8}>
                <Field
                  id='date_type'
                  name='date_type'
                  label='Tipo de data'
                  options={dateTypes}
                  component={SelectFormField}
                  fullWidth
                />
              </Col>
              <Col xs={24} sm={12} md={16}>
                <Field
                  id='date'
                  label='Período'
                  name='date'
                  component={DateRangePickerFormField}
                  isPresetLonger
                  isOutsideRange={isOutsideRange}
                />
              </Col>
            </Row>
          </Col>
          <Col xs={24} md={12}>
            <Field id='product_id' name='product_id'>
              {(props: FieldProps) => (
                <SelectProduct
                  single={false}
                  {...props}
                  label='Produtos'
                  endpoint='/reports/sales-filter-products'
                  ref={selectProductRef}
                />
              )}
            </Field>
          </Col>
          <Col xs={24} sm={8} md={8}>
            <Field id='invoice_id' name='invoice_id' label='Fatura' mask='number' component={TextFormField} fullWidth />
          </Col>
          <Col xs={24} sm={16} md={8}>
            <Field
              id='motive_id'
              name='motive_id'
              label='Motivo'
              loading={loadingMotivesOptions}
              options={motivesOptions}
              component={SelectFormField}
              multiple
              fullWidth
            />
          </Col>
          <Col xs={12} md={4} className='flex items-end md:pt-5'>
            <Button id='clean-filter-button' block onClick={handleClean} type='text' disabled={isCleanDisabled}>
              Limpar
            </Button>
          </Col>
          <Col xs={12} md={4} className='flex items-end md:pt-5'>
            <Button
              id='filter-button'
              type='primary'
              disabled={isFilterDisabled}
              htmlType='submit'
              loading={isSubmitting || isLoading}
              icon={<FilterOutlineIcon className='mr-0 text-sm' />}
              block
            >
              Filtrar
            </Button>
          </Col>
        </Row>
      </div>
    </FormikForm>
  );
};
